import { RECORDING_TIMESLICE_MS, formatTimestamp } from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { meetingsApi } from '../lib/api.js';
import { ChunkQueue, type ChunkQueueState } from '../lib/chunkQueue.js';
import { pendingUpload } from '../lib/chunkStore.js';
import { clientLog } from '../lib/clientLog.js';

type Phase = 'idle' | 'recording' | 'finishing' | 'stopFailed' | 'done';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

/** No chunk within two timeslices means the capture has died, not that it is quiet. */
const WATCHDOG_MS = RECORDING_TIMESLICE_MS * 2;

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
}

const EMPTY_QUEUE_STATE: ChunkQueueState = {
  uploaded: 0,
  pending: 0,
  failed: 0,
  stalled: false,
  online: true,
  lastError: null,
};

export function RecorderPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [captureLost, setCaptureLost] = useState(false);
  const [wakeLockHeld, setWakeLockHeld] = useState(false);
  const [resumable, setResumable] = useState<number | null>(null);
  const [queueState, setQueueState] = useState<ChunkQueueState>(EMPTY_QUEUE_STATE);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const queueRef = useRef<ChunkQueue | null>(null);
  const chunkIndexRef = useRef(0);
  const lastDataAtRef = useRef(0);
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const timerRef = useRef<number | null>(null);
  const watchdogRef = useRef<number | null>(null);
  /** One diagnostic line per watchdog trip, not one every half-timeslice. */
  const watchdogTrippedRef = useRef(false);

  const { data: meetingData } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  // Offer to finish an upload a previous session left behind.
  useEffect(() => {
    let cancelled = false;
    if (!id) return undefined;
    void pendingUpload(id).then((pending) => {
      if (!cancelled && pending && pending.chunks.length > 0) {
        setResumable(pending.chunks.length);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const releaseWakeLock = useCallback(async (): Promise<void> => {
    try {
      await wakeLockRef.current?.release();
    } catch {
      /* already released */
    }
    wakeLockRef.current = null;
    setWakeLockHeld(false);
  }, []);

  const requestWakeLock = useCallback(async (): Promise<void> => {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock) return;
    try {
      wakeLockRef.current = await nav.wakeLock.request('screen');
      setWakeLockHeld(true);
    } catch {
      setWakeLockHeld(false);
    }
  }, []);

  const clearWatchdog = useCallback((): void => {
    if (watchdogRef.current) window.clearInterval(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  // Re-acquire the wake lock when the tab comes back to the foreground.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && phase === 'recording') void requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [phase, requestWakeLock]);

  // Warn before leaving while a recording is live or still uploading.
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent): void => {
      if (phase === 'recording' || phase === 'finishing' || queueState.pending > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [phase, queueState.pending]);

  useEffect(
    () => () => {
      queueRef.current?.dispose();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (watchdogRef.current) window.clearInterval(watchdogRef.current);
      void releaseWakeLock();
    },
    [releaseWakeLock],
  );

  const start = async (restart = false): Promise<void> => {
    setError(null);
    setCaptureLost(false);
    const mimeType = pickMimeType();
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
      setError(t('recorder.unsupported'));
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
    } catch {
      setError(t('recorder.micDenied'));
      return;
    }

    let attempt = 0;
    try {
      const started = await meetingsApi.startRecording(id, restart);
      attempt = started.attempt ?? 0;
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      setError(err instanceof Error ? err.message : t('common.error'));
      return;
    }

    streamRef.current = stream;
    chunkIndexRef.current = 0;
    queueRef.current = new ChunkQueue(id, attempt, mimeType, setQueueState);
    setResumable(null);

    // A track that ends (device unplugged, OS grab) stops producing audio
    // silently; surface it instead of recording nothing.
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', () => {
        clientLog('recorder.track.ended', { label: track.label }, { level: 'error', meetingId: id });
        setCaptureLost(true);
      });
      track.addEventListener('mute', () => {
        clientLog('recorder.track.mute', { label: track.label }, { level: 'warn', meetingId: id });
        setCaptureLost(true);
      });
      track.addEventListener('unmute', () => {
        clientLog('recorder.track.unmute', { label: track.label }, { level: 'info', meetingId: id });
        setCaptureLost(false);
      });
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 });
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      setError(err instanceof Error ? err.message : t('recorder.unsupported'));
      return;
    }

    recorder.ondataavailable = (event) => {
      const now = Date.now();
      const sincePrevious = now - lastDataAtRef.current;
      lastDataAtRef.current = now;
      watchdogTrippedRef.current = false;
      if (event.data && event.data.size > 0) {
        clientLog(
          'recorder.dataavailable',
          { index: chunkIndexRef.current, size: event.data.size, msSincePrevious: sincePrevious },
          { level: 'info', meetingId: id },
        );
        queueRef.current?.push(chunkIndexRef.current, event.data);
        chunkIndexRef.current += 1;
        setCaptureLost(false);
      }
    };
    recorder.onerror = (event) => {
      const detail = (event as Event & { error?: DOMException }).error;
      clientLog(
        'recorder.error',
        { errorName: detail?.name ?? 'unknown', errorMessage: detail?.message ?? '' },
        { level: 'error', meetingId: id },
      );
      setError(detail?.message ?? t('recorder.recorderError'));
      setCaptureLost(true);
    };

    recorder.start(RECORDING_TIMESLICE_MS);
    clientLog(
      'recorder.start',
      { mimeType, timesliceMs: RECORDING_TIMESLICE_MS, restart },
      { level: 'info', meetingId: id },
    );
    recorderRef.current = recorder;
    lastDataAtRef.current = Date.now();

    setPhase('recording');
    setElapsed(0);
    timerRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    watchdogRef.current = window.setInterval(() => {
      const since = Date.now() - lastDataAtRef.current;
      if (since <= WATCHDOG_MS) return;
      if (!watchdogTrippedRef.current) {
        watchdogTrippedRef.current = true;
        clientLog(
          'recorder.watchdog.trip',
          { msSinceLastData: since, state: recorder.state },
          { level: 'error', meetingId: id },
        );
      }
      setCaptureLost(true);
    }, RECORDING_TIMESLICE_MS / 2);
    void requestWakeLock();
  };

  /** Shared by Stop and by "retry finishing" after a failed stop. */
  const finish = async (): Promise<void> => {
    setError(null);
    setPhase('finishing');
    queueRef.current?.markStopping();
    clientLog(
      'recorder.stop.pressed',
      { chunks: chunkIndexRef.current, elapsedSec: elapsed, pending: queueState.pending },
      { level: 'info', meetingId: id },
    );
    if (timerRef.current) window.clearInterval(timerRef.current);
    clearWatchdog();

    try {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        await new Promise<void>((resolve) => {
          recorder.onstop = () => resolve();
          try {
            recorder.stop();
          } catch {
            resolve();
          }
        });
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      await releaseWakeLock();

      const queue = queueRef.current;
      if (queue) {
        await queue.flush();
        const result = await queue.complete(chunkIndexRef.current, elapsed);
        clientLog(
          'recorder.complete.ok',
          { totalChunks: chunkIndexRef.current, alreadyComplete: result.alreadyComplete ?? false },
          { level: 'info', meetingId: id },
        );
        await queue.discardPersisted();
      } else {
        await meetingsApi.completeRecording(id, chunkIndexRef.current, elapsed);
        clientLog(
          'recorder.complete.ok',
          { totalChunks: chunkIndexRef.current, viaQueue: false },
          { level: 'info', meetingId: id },
        );
      }

      setPhase('done');
      navigate(`/meetings/${id}`);
    } catch (err) {
      // Leave the take recoverable: the chunks are still on the server and in
      // IndexedDB, so the operator retries finishing rather than re-recording.
      clientLog(
        'recorder.complete.failed',
        {
          totalChunks: chunkIndexRef.current,
          errorName: err instanceof Error ? err.name : 'unknown',
          errorMessage: err instanceof Error ? err.message : String(err),
        },
        { level: 'error', meetingId: id },
      );
      setError(err instanceof Error ? err.message : t('common.error'));
      setPhase('stopFailed');
    }
  };

  const resume = async (): Promise<void> => {
    setError(null);
    const resumed = await ChunkQueue.resume(id, setQueueState);
    if (!resumed) {
      setResumable(null);
      return;
    }
    queueRef.current?.dispose();
    queueRef.current = resumed.queue;
    chunkIndexRef.current = resumed.chunks.length;
    setResumable(null);
    await finish();
  };

  const busy = phase === 'finishing';

  return (
    <>
      <h1>{meetingData?.meeting.title ?? t('recorder.title')}</h1>

      {error && <div className="banner error">{error}</div>}
      {!queueState.online && <div className="banner">{t('recorder.offline')}</div>}
      {queueState.stalled && <div className="banner error">{t('recorder.stalled')}</div>}
      {captureLost && phase === 'recording' && (
        <div className="banner error">{t('recorder.captureLost')}</div>
      )}
      {resumable !== null && phase === 'idle' && (
        <div className="banner">
          {t('recorder.resumeAvailable', { count: resumable })}{' '}
          <button type="button" className="link" onClick={() => void resume()}>
            {t('recorder.resume')}
          </button>
        </div>
      )}

      <div className="card" style={{ textAlign: 'center' }}>
        <div>
          {phase === 'recording' ? (
            <span className="chip live">
              <span className="pulse" />
              {t('recorder.live')}
            </span>
          ) : (
            <span className="chip">
              {t(
                `recorder.${phase === 'idle' ? 'idle' : phase === 'stopFailed' ? 'stopFailed' : 'finishing'}`,
              )}
            </span>
          )}
        </div>

        <div className="elapsed">{formatTimestamp(elapsed)}</div>

        {phase === 'idle' && (
          <button type="button" className="gold" onClick={() => void start(false)}>
            {t('recorder.start')}
          </button>
        )}
        {phase === 'recording' && (
          <button type="button" className="danger" onClick={() => void finish()}>
            {t('recorder.stop')}
          </button>
        )}
        {phase === 'finishing' && (
          <>
            <p className="muted">{t('recorder.finishing')}</p>
            {queueState.pending > 0 && (
              <p className="muted">
                {t('recorder.uploadingPiece', {
                  current: queueState.uploaded + 1,
                  total: queueState.uploaded + queueState.pending,
                })}
              </p>
            )}
            {queueState.lastError && (
              <p className="muted" style={{ color: 'var(--danger)' }}>
                {t('recorder.lastError')}: {queueState.lastError}
              </p>
            )}
          </>
        )}
        {/* Never offer Start here: a second take would be spliced onto this one. */}
        {phase === 'stopFailed' && (
          <>
            <p className="muted">{t('recorder.stopFailedHelp')}</p>
            <button type="button" className="gold" disabled={busy} onClick={() => void finish()}>
              {t('recorder.retryFinish')}
            </button>
          </>
        )}
        {phase === 'done' && <p className="muted">{t('recorder.done')}</p>}
      </div>

      <div className="card">
        <table>
          <tbody>
            <tr>
              <td>{t('recorder.uploaded')}</td>
              <td>{queueState.uploaded}</td>
            </tr>
            <tr>
              <td>{t('recorder.pending')}</td>
              <td>{queueState.pending}</td>
            </tr>
            {queueState.lastError && (
              <tr>
                <td style={{ color: 'var(--danger)' }}>
                  {queueState.stalled ? t('recorder.retrying') : t('recorder.lastError')}
                </td>
                <td style={{ color: 'var(--danger)' }}>{queueState.lastError}</td>
              </tr>
            )}
            {wakeLockHeld && (
              <tr>
                <td colSpan={2} className="muted">
                  {t('recorder.wakeLock')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0 }}>
          {t('recorder.keepOpen')}
        </p>
      </div>
    </>
  );
}
